import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

const TermsPage = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen brand-page-bg text-slate-900">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-0 h-96 w-96 rounded-full bg-[#dbe9ff] blur-3xl" />
        <div className="absolute top-20 right-0 h-80 w-80 rounded-full bg-[#f8efe2] blur-3xl" />
        <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[#e6f4ee] blur-3xl" />
      </div>

      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-[#fbfdff]/92 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/ichurch-logo.svg" alt="iChurch Data" className="h-8 w-8 rounded-xl object-cover" />
            <span className="text-lg font-semibold tracking-tight text-slate-900">iChurch Data</span>
          </Link>

          <div className="flex items-center gap-2">
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/privacy">Privacy</Link>
            </Button>
            <Button variant="ghost" className="hidden text-slate-700 md:inline-flex" asChild>
              <Link to="/terms">Terms</Link>
            </Button>
            <Button variant="ghost" className="text-slate-700" asChild>
              <Link to="/login">Sign in</Link>
            </Button>
            <Button className="bg-[#1e3a8a] hover:bg-[#162d70]" asChild>
              <Link to="/register">Sign Up</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-4xl px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#1e3a8a] font-medium hover:underline mb-4">
          <ChevronLeft className="h-4 w-4" /> Back to Home
        </Link>
        <h1 className="text-3xl font-bold">Terms of Service</h1>
        <p className="mt-2 text-sm text-slate-600">Effective Date: April 2026</p>

        <section className="mt-8 space-y-6 text-sm leading-6 text-slate-700">
          <p>
            These Terms of Service ("Terms") constitute a legally binding agreement between iChurch Data LLC
            ("iChurch Data," "we," "us," or "our") and the organization or individual ("Customer," "you," or
            "your") accessing or using the iChurch Data platform (the "Service"). By creating an account or using
            the Service, you agree to be bound by these Terms.
          </p>

          <div>
            <h2 className="text-base font-semibold text-slate-900">1. Description of Service</h2>
            <p>
              iChurch Data is a subscription-based church management software platform that provides tools for
              member management, attendance tracking, event management, internal communications, and
              financial record keeping. The Service is intended solely for use by church organizations and similar
              nonprofit religious entities.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">2. Eligibility and Account Registration</h2>
            <p>
              To use the Service, you must: (a) represent a valid church or religious organization; (b) be at least
              18 years of age and have legal authority to bind your organization to these Terms; (c) provide
              accurate, complete, and current registration information; and (d) maintain the accuracy of that
              information throughout your use of the Service. By accepting these Terms, you represent and
              warrant that you meet all of the above requirements.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">3. Subscription Plans and Billing</h2>
            <p>
              Access to the Service requires a paid subscription. Plans are billed monthly based on your
              organization's active member count, according to the following tiers: (a) Up to 300 members: $49
              per month; (b) 301 to 500 members: $79 per month; (c) 501 to 1,000 members: $99 per month;
              (d) 1,000 or more members: $149 per month. Billing is charged automatically at the start of each
              billing cycle. You authorize iChurch Data to charge your payment method on file for the applicable
              subscription fee. All fees are non-refundable except as required by law or as expressly stated in
              these Terms. iChurch Data reserves the right to adjust pricing upon at least 30 days written notice
              to the Customer.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">4. Account Security and Responsibilities</h2>
            <p>
              You are solely responsible for: (a) maintaining the confidentiality of your account credentials and
              preventing unauthorized access; (b) assigning user roles and permissions appropriately within your
              organization; (c) all activity that occurs under your account; and (d) ensuring that your organization
              has the legal authority to collect, enter, and process member information through the Service. You
              agree to notify us immediately at contact@ichurchdata.com of any unauthorized use of your account
              or any suspected security breach.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">5. Acceptable Use</h2>
            <p>
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You shall
              not: (a) use the Service in any way that violates applicable law or regulation; (b) upload or transmit
              unlawful, harmful, defamatory, or infringing data; (c) attempt unauthorized access to the Service or
              related systems; (d) use the Service to store or process sensitive personal information beyond what is
              necessary for legitimate church management purposes; (e) resell, sublicense, or otherwise make the
              Service available to any third party without prior written consent; or (f) interfere with or disrupt the
              integrity or performance of the Service.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">6. Data Ownership and Privacy</h2>
            <p>
              You retain ownership of all data your organization inputs into the Service ("Customer Data"). By
              using the Service, you grant iChurch Data a limited, non-exclusive license to access and process
              Customer Data solely as necessary to provide and maintain the Service. iChurch Data does not
              claim any ownership rights over Customer Data. Our collection and use of personal information is
              governed by our Privacy Policy, which is incorporated into these Terms by reference.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">7. Suspension and Termination</h2>
            <p>
              iChurch Data reserves the right to suspend or terminate your account, with or without notice, in the
              following circumstances: (a) non-payment of subscription fees; (b) violation of these Terms or our
              acceptable use policies; (c) conduct that poses a security risk to the Service or other users; or (d) as
              required by law or court order. Upon termination, access to the Service will cease. Customer Data
              will be retained for 90 days following termination to allow for export, after which it will be securely
              deleted. You may terminate your subscription at any time by contacting contact@ichurchdata.com.
              Termination does not entitle you to a refund of any prepaid fees.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">8. Intellectual Property</h2>
            <p>
              The Service, including all software, designs, features, and content provided by iChurch Data, is the
              exclusive property of iChurch Data LLC and is protected by applicable intellectual property laws.
              These Terms do not grant any rights to iChurch Data trademarks, logos, or proprietary technology.
              You may not copy, modify, distribute, or create derivative works based on the Service without prior
              written consent.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">9. Disclaimer of Warranties</h2>
            <p>
              The Service is provided on an "as-is" and "as-available" basis without warranties of any kind,
              express or implied, including implied warranties of merchantability, fitness for a particular purpose,
              or non-infringement. iChurch Data does not warrant that the Service will be uninterrupted,
              error-free, or free of harmful components. You assume full responsibility for your use of the Service.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">10. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, iChurch Data LLC shall not be liable for indirect,
              incidental, special, consequential, or punitive damages arising out of or related to your use of the
              Service. In no event shall iChurch Data's total cumulative liability exceed the total fees paid by you
              to iChurch Data during the three-month period immediately preceding the event giving rise to the
              claim. This limitation applies regardless of the theory of liability and even if advised of possible
              damages.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless iChurch Data LLC and its officers, directors,
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses,
              including reasonable attorneys' fees, arising out of or related to: (a) your use of the Service; (b) your
              violation of these Terms; (c) your organization's collection or processing of member data; or (d) any
              claim that your Customer Data infringes third-party rights.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">12. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. If we make material changes, we will notify you via
              email or a prominent notice within the platform at least 30 days before the changes take effect.
              Continued use after the effective date constitutes acceptance of revised Terms. If you do not agree,
              discontinue use before the effective date.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">13. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of the State of South Carolina, without regard to conflict of
              law provisions. Any disputes arising out of or relating to these Terms or the Service are subject to the
              exclusive jurisdiction of the state and federal courts located in Charleston County, South Carolina.
              You waive any objection to venue in such courts.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">14. Severability</h2>
            <p>
              If any provision of these Terms is found unenforceable or invalid under applicable law, that
              provision will be limited or eliminated to the minimum extent necessary, and remaining provisions
              will continue in full force and effect.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">15. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and any applicable service order or agreement,
              constitute the entire agreement between you and iChurch Data LLC with respect to the Service and
              supersede all prior or contemporaneous agreements, representations, or understandings.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold text-slate-900">16. Contact Us</h2>
            <p>iChurch Data LLC</p>
            <p>1260 Bees Ferry Rd, Unit 204, Charleston, SC 29414</p>
            <p>Email: contact@ichurchdata.com</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default TermsPage;
